

import { Component, Vue, Prop, Inject, Provide, Emit } from "vue-property-decorator"

import vSelect from "vue-select"
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import { BModal, BvModalEvent } from "bootstrap-vue"

import DataForm from "./DataForm.vue"
import FieldPermissions from "./Permissions/FieldPermissions.vue"
import MapDisplay from "@/components/Controls/Map.vue"

import { MapProvider } from "@/services/mapProvider"
import ConfigurationColumn from "@/types/configurationColumn"
import { Getter } from "vuex-class"
import { TenantFeatures } from "@/types/tenantFeatures"
import { get } from "lodash"

@Component({
	components: {
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"field-permissions": FieldPermissions,
		"data-form": DataForm,
		"map-display": MapDisplay
	}
})
export default class DataModal extends Vue {
	$refs!: {
		editModal: BModal;
		scrollbar: any;
		cancelEditModal: BModal;
	}

	@Inject({ from: "mapProvider", default: null }) parentMapProvider!: MapProvider;
	@Provide("mapProvider") mapProvider: MapProvider = new MapProvider();

	@Prop()
	title!: string;

	@Prop({ default: () => null })
	public titleColumnKey!: string | null

	@Prop()
	columns!: ConfigurationColumn[];

	@Prop()
	canEdit!: boolean;

	@Prop({ type: Boolean, default: false })
	allowMap!: boolean;

	@Prop({ type: String, default: "xl" })
	public modalSize!: string;

	@Prop({ default: () => null })
	public deleteItemName!: ((item: any) => string) | null

	@Prop()
	public deleteDetails!: (item: any) => any;

	@Prop()
	public canDeleteItem!: (item: any) => boolean;

	@Prop()
	public canConfirmDelete!: (item: any) => boolean;

	@Prop({ type: String, default: null })
	public cancelEditWarning!: string | null

	@Prop({ type: Boolean, default: true })
	public closeOnSave!: boolean

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	public deleteConfirmButtonDisabled: boolean = false;
	public saveButtonDisabled: boolean = false;

	itemOverrideColumns: any[] | null = null;
	item: any = null;

	canDelete: boolean = false;
	confirmDelete: boolean = true;
	deleteConfirmText: string = "";
	deleteDetailsItem: any = null;
	deleting: boolean = false;

	public valid: boolean = false;

	tenantFeatures = TenantFeatures

	get itemInUse (): boolean {
		if (this.deleteDetailsItem) {
			// If it's an array, return false if it has a length greater than 0
			if (Array.isArray(this.deleteDetailsItem) && this.deleteDetailsItem.length > 0) {
				return true
			}

			// If it's an object, check if any value is an array with a length greater than 0
			if (typeof this.deleteDetailsItem === "object") {
				for (const key in this.deleteDetailsItem) {
					if (Array.isArray(this.deleteDetailsItem[key]) && this.deleteDetailsItem[key].length > 0) {
						return true
					}
				}
			}
		}
		return false
	}

	get itemCanBeDeleted (): boolean {
		return !(this.getIsFeatureEnabled(TenantFeatures.PreventInUseDeletion) && this.itemInUse) && this.confirmDelete
	}

	public get canEditTitle (): boolean {
		if (!this.titleColumn || !this.item) return false

		if (typeof (this.titleColumn.readonly) === "boolean") {
			return !this.titleColumn.readonly
		} else if (typeof (this.titleColumn.readonly) === "function") {
			return !this.titleColumn.readonly(this.item)
		}
		return !this.titleColumn.readonly
	}

	public get itemTitle (): string | null {
		if (!this.titleColumnKey) return null
		return get(this.item, this.titleColumnKey)
	}

	public async open (item: any, itemOverrideColumns: any[] | null = null) {
		this.canDelete = this.canDeleteItem ? await this.canDeleteItem(item) : true
		this.confirmDelete = this.canConfirmDelete ? await this.canConfirmDelete(item) : true

		this.deleting = false
		this.emitDeleteStatus(this.deleting)
		this.deleteConfirmText = ""

		this.itemOverrideColumns = itemOverrideColumns
		this.item = item
		this.$refs.editModal.show()
	}

	public get displayColumns () {
		const columns = this.itemOverrideColumns ? this.itemOverrideColumns : this.columns
		if (this.titleColumnKey) {
			return columns.filter(c => c.key !== this.titleColumnKey)
		}

		return columns
	}

	public get titleColumn (): ConfigurationColumn | null {
		return this.titleColumnKey
			? this.columns.find(c => c.key === this.titleColumnKey) || null
			: null
	}

	public get showMap () {
		return this.allowMap && this.columns.some(
			c => c.dataType === "map-marker" ||
			c.dataType === "map-path" ||
			c.dataType === "map-region"
		)
	}

	public get isDeleteButtonDisabled () : boolean {
		return this.item && (this.deleteConfirmButtonDisabled || (
			this.item.name
				? this.deleteConfirmText !== this.item.name
				: this.item.title ? this.deleteConfirmText !== this.item.title : false)
		)
	}

	public save () {
		this.emitSave(this.item)

		if (this.closeOnSave) {
			this.hide()
		}
	}

	public async deleteItem () {
		if (this.deleteDetails) {
			this.deleteDetailsItem = await this.deleteDetails(this.item)
		}
		this.deleting = true
		this.emitDeleteStatus(this.deleting)
	}

	public async deleteConfirm () {
		if (!this.isDeleteButtonDisabled) {
			this.emitDelete(this.item)
			this.hide()
		}
	}

	public isSaveButtonDisabled () : boolean {
		return this.saveButtonDisabled || !this.valid
	}

	private hide () {
		this.$refs.editModal.hide()
	}

	private cancelEdit (okAction: (() => void) | null = null, bvModalEvent: BvModalEvent | null = null) {
		if (!this.cancelEditWarning) {
			if (okAction) {
				okAction()
			}

			this.emitEditCancelled()
		} else {
			if (bvModalEvent) {
				bvModalEvent.preventDefault()
			}

			this.$refs.cancelEditModal.show()
		}
	}

	private confirmCancelEdit () {
		this.$refs.editModal.hide()
		this.emitEditCancelled()
	}

	@Emit("editCancelled")
	private emitEditCancelled (): void {
		// Emit event
	}

	@Emit("delete")
	private emitDelete (item: any): any {
		return item
	}

	@Emit("deleteStatus")
	private emitDeleteStatus (status: boolean): boolean {
		return status
	}

	@Emit("unsaved-item-change")
	public handleChange (): any {
		return this.item
	}

	@Emit("save")
	private emitSave (item: any): any {
		return item
	}
}

